import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    NgZone,
    OnDestroy,
    OnInit,
    Renderer2,
    Signal,
    ViewChild,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { fromEvent, merge, Subscription, timer } from "rxjs";
import {
    distinctUntilChanged,
    filter,
    map,
    startWith,
    switchMap,
    tap,
} from "rxjs/operators";

import { LayoutFacade } from "@hermes/aphrodite/layout";
import { Context, WINDOW } from "@hermes/app-core";
import { GridFacade, GridFilterGroup } from "@hermes/states/grid-filters-state";

import { GridFilterSectionComponent } from "../grid-filter-section/grid-filter-section.component";
import { GridFilterSectionHeaderComponent } from "../grid-filter-section-header/grid-filter-section-header.component";
import { GridFilterTagsComponent } from "../grid-filter-tags/grid-filter-tags.component";

@Component({
    selector: "h-grid-filter-bar",
    standalone: true,
    templateUrl: "./grid-filter-bar.component.html",
    styleUrls: ["./grid-filter-bar.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        GridFilterSectionComponent,
        GridFilterSectionHeaderComponent,
        GridFilterTagsComponent,
    ],
})
export class GridFilterBarComponent implements OnInit, OnDestroy {
    @ViewChild("filterBarMenu")
    public filterBarMenu!: ElementRef;
    @ViewChild("filterBarTags")
    public filterBarTags!: ElementRef;

    public mainHeaderHeight = 0;
    public startPosition = 0;
    public isServer = true;
    public filterGroups: Signal<GridFilterGroup[]> = toSignal(
        this.gridFacade.filterGroups$,
        {
            initialValue: [],
        },
    );

    private subscription: Subscription = new Subscription();
    private resizeInProgress = false;

    constructor(
        public detector: ChangeDetectorRef,
        @Inject(WINDOW) private window: Window,
        private layoutFacade: LayoutFacade,
        private ngZone: NgZone,
        private renderer: Renderer2,
        private context: Context,
        private gridFacade: GridFacade,
    ) {}

    public ngOnInit(): void {
        this.isServer = this.context.isInServerMode();
        this.subscription.add(
            this.layoutFacade.headerMainHeight$
                .pipe(distinctUntilChanged())
                .subscribe((headerMainHeight: number) => {
                    this.mainHeaderHeight = headerMainHeight;
                    if (this.filterBarMenu) {
                        this.updateFilterBarPosition();
                    }
                    this.detector.markForCheck();
                }),
        );

        if (this.context.isInBrowserMode()) {
            this.ngZone.runOutsideAngular(() => {
                // Resize event triggers lots of scroll events.
                // We'll ignore them to avoid flashing effect on resize.
                const resize$ = fromEvent(this.window, "resize").pipe(
                    tap(() => (this.resizeInProgress = true)),
                    switchMap(() => timer(300).pipe(map(() => false))),
                    startWith(false),
                    tap((inProgress) => (this.resizeInProgress = inProgress)),
                );
                const scroll$ = fromEvent(this.window, "scroll").pipe(
                    filter(() => !this.resizeInProgress),
                    tap(() => this.updateFilterBarPosition()),
                );
                this.subscription.add(merge(resize$, scroll$).subscribe());
            });
        }
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    private updateFilterBarPosition(): void {
        const windowScrollY = this.window.scrollY;
        const isScrollingToTop = this.startPosition > windowScrollY;
        const filterBarMenuHeight =
            this.filterBarMenu.nativeElement.offsetHeight;

        // When scrolling to top, we need the filter bar menu to be visible
        // so we move it under the main header.
        // Otherwise, it must always be hidden behing the main header.
        const filterBarMenuTopValue =
            windowScrollY > 0 && isScrollingToTop
                ? this.mainHeaderHeight
                : this.mainHeaderHeight - filterBarMenuHeight;

        // Filter bar tags must always be visible so we move
        // it under the main header and under the filter menu (if visible).
        const filterBarTagsTopValue =
            filterBarMenuTopValue + filterBarMenuHeight;

        this.setPositionTopValue(
            this.filterBarMenu.nativeElement,
            filterBarMenuTopValue,
        );

        this.setPositionTopValue(
            this.filterBarTags.nativeElement,
            filterBarTagsTopValue,
        );
        this.startPosition = windowScrollY;
    }

    private setPositionTopValue(nativeElement: ElementRef, top: number): void {
        this.renderer.setStyle(nativeElement, "top", `${top}px`);
    }
}
